import { useQuery } from '@tanstack/react-query';
import { useDemoMode } from '@/context/DemoModeContext';
import {
  OCRMetricsResponse,
  OCRActivityLogsResponse,
  OCRTimeRange,
} from './ocr-type';
import {
  generateMockOCRMetricsResponse,
  generateMockActivityLogsResponse,
} from '@/mock/gpu-instance/ocr';

interface UseOCRMetricsParams {
  instanceId: string;
  timeRange: OCRTimeRange;
  customMonth?: string;
}

interface UseOCRActivityLogsParams {
  instanceId: string;
  page?: number;
  pageSize?: number;
}

// Hook to fetch OCR metrics
export const useOCRMetrics = ({
  instanceId,
  timeRange,
  customMonth,
}: UseOCRMetricsParams) => {
  const { isDemoMode } = useDemoMode();

  return useQuery<OCRMetricsResponse>({
    queryKey: ['ocr-metrics', instanceId, timeRange, customMonth],
    queryFn: async () => {
      if (isDemoMode) {
        // Simulate API delay
        await new Promise((resolve) => setTimeout(resolve, 500));
        return generateMockOCRMetricsResponse(timeRange, customMonth);
      }

      // TODO: Implement actual API call for non-demo mode
      throw new Error('OCR metrics API not implemented for non-demo mode');
    },
    enabled: !!instanceId,
    staleTime: 30000, // 30 seconds
  });
};

// Hook to fetch OCR activity logs
export const useOCRActivityLogs = ({
  instanceId,
  page = 1,
  pageSize = 20,
}: UseOCRActivityLogsParams) => {
  const { isDemoMode } = useDemoMode();

  return useQuery<OCRActivityLogsResponse>({
    queryKey: ['ocr-activity-logs', instanceId, page, pageSize],
    queryFn: async () => {
      if (isDemoMode) {
        // Simulate API delay
        await new Promise((resolve) => setTimeout(resolve, 300));
        return generateMockActivityLogsResponse(page, pageSize, 150);
      }

      // TODO: Implement actual API call for non-demo mode
      throw new Error('OCR activity logs API not implemented for non-demo mode');
    },
    enabled: !!instanceId,
    staleTime: 30000, // 30 seconds
  });
};
